<script lang="ts" setup>
import { ref, onMounted, inject, computed, watch } from 'vue';
import axiosInstance from '@/plugins/axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useRouter, useRoute } from 'vue-router';

const gF = inject('gF') as any;
const router = useRouter();
const route = useRoute();

const customizationOptions = ref<any[]>([]);

const searchTerm = ref('');
const categoryFilter = ref('all');

const rowsPerPage = ref(15)

const onRowReorder = async (event: any) => {
  const reordered = [...event.value];
  const grouped: Record<string, any[]> = {};

  for (const item of reordered) {
    const cat = item.category || 'uncategorized';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(item);
  }

  const updated = Object.entries(grouped).flatMap(([_, items]) =>
    items.map((item, index) => ({
      ...item,
      order: index
    }))
  );

  customizationOptions.value = updated;

  try {
    await axiosInstance.put('/admin/customization/reorder', updated);
  } catch (err) {
    console.error('Failed to update order:', err);
  }
};

onMounted(async () => {
  gF.startLoading();
  const response = await axiosInstance.get('/admin/customizationOptions');

  customizationOptions.value = response.data.sort((a, b) => {
    if (a.category === b.category) {
      return a.order - b.order;
    }
    return a.category.localeCompare(b.category);
  });

  rowsPerPage.value = Number(route.query.showRows) || 25;
  const initialCategory = (route.query.category as string) || 'all';
  categoryFilter.value = initialCategory;
  gF.endLoading();
});

watch(categoryFilter, (val) => {
  const newQuery = { ...route.query } as Record<string, any>;
  if (val && val !== 'all') {
    newQuery.category = val;
  } else {
    delete newQuery.category;
  }
  router.replace({ query: newQuery });
});

const baseCategories = [
  'Main Question Box',
  'Main Page Appearance',
  'Infoboxes',
  'Result Page',
  'Imprint and Data Privacy',
  'Result PDF',
  'Main Page Texts',
  'FAQ',
  'Social Media',
  'SEO',
  'Lead Generation',
  'Misc'
];

const categoryOptions = computed(() => {
  const categories = new Set<string>(baseCategories);
  customizationOptions.value.forEach((opt) => {
    if (opt.category) categories.add(opt.category);
  });
  return [{ label: 'All', value: 'all' }, ...Array.from(categories).map((cat) => ({ label: cat, value: cat }))];
});

const filteredOptions = computed(() => {
  const term = searchTerm.value.trim().toLowerCase();
  return customizationOptions.value.filter((opt) => {
    const matchesCategory = categoryFilter.value === 'all' || opt.category === categoryFilter.value;
    const matchesSearch = !term || (opt.title || '').toLowerCase().includes(term) || (opt.key || '').toLowerCase().includes(term);
    return matchesCategory && matchesSearch;
  });
});

</script>


<template>
  <div class="customazation-overview">
            <div class="row mb-5 col-12 justify-content-between">
            <div class="col text-center">
                <Button class="p-button-tuev w-50" label="Add" @click="router.push({ name: 'CustomizationDetailView', params: { id: 'new' } })" iconPos="left">
                    <template #icon>
                        <font-awesome-icon :icon="['fas', 'plus']" />
                    </template>
                </Button>
            </div>
        </div>

      <div class="row mb-3 col-12 justify-content-between align-items-center">
        <div class="col-12 col-md-6 mb-2 mb-md-0">
          <InputText v-model="searchTerm" class="w-100" placeholder="Search by title or key" />
        </div>
        <div class="col-12 col-md-4">
          <Select v-model="categoryFilter" class="w-100" :options="categoryOptions" optionLabel="label" optionValue="value"
            placeholder="Filter by category" />
        </div>
      </div>

<DataTable
    :value="filteredOptions"
  @rowReorder="onRowReorder"
  :reorderableRows="true"
  rowGroupMode="subheader"
  groupRowsBy="category"
  sortField="category"
  :sortOrder="1"
  :paginator="true"
  :rows="rowsPerPage"
  :showHeaders="false"
  :rowsPerPageOptions="[5, 10, 25, 50, 100, 150]"
>

  <template #groupheader="slotProps">
    <tr>
      <td colspan="5" class="bg-gray-100 font-semibold">
        {{ slotProps.data.category }}
      </td>
    </tr>
  </template>

      <Column rowReorder headerStyle="width: 3rem" />
      <Column field="order" header="Order"></Column>
      <Column field="title" header="Title"></Column>
      <Column field="category" header="Category"></Column>
      <Column field="fieldType" header="Type"></Column>
      <Column header="Actions">
        <template #body="slotProps">
          <Button @click="() => router.push({ name: 'CustomizationDetailView', params: { id: slotProps.data._id } })" severity="info">
            <template #icon>
              <FontAwesomeIcon icon="fa-solid fa-pen-to-square" />
            </template>
          </Button>
          <Button severity="danger" class="ml-2">
            <template #icon>
              <FontAwesomeIcon icon="fa-solid fa-trash" />
            </template>
          </Button>
        </template>
      </Column>
    </DataTable>
  </div>
</template>